import type { ProjectNode } from '@/shared/types/projectData';

type Patch = Partial<ProjectNode>;
type NoteTitleDraft = { value: string; previous: string };
export type NoteSaveStatus = 'idle' | 'pending' | 'saving' | 'failed';
export const noteDraftAccountPrefix = (actorId: string, effectiveId: string, viewAs: string | null): string =>
  JSON.stringify([actorId, effectiveId, viewAs]).slice(0, -1) + ',';
export type NoteDraftSnapshot = {
  changes: Patch;
  before: Patch;
  status: 'idle' | 'saving' | 'failed';
  revision: number;
  titleDraft: NoteTitleDraft | null;
};
export type NoteWriter = (changes: Patch) => Promise<void>;
type RecordState = {
  snapshot: NoteDraftSnapshot;
  pending: { changes: Patch; revision: number; write: NoteWriter } | null;
  running: boolean;
  completion: Promise<void> | null;
  listeners: Set<() => void>;
};
const STORAGE_PREFIX = 'pm-note-draft-v1:';
const isPatch = (value: unknown): value is Patch => Boolean(value && typeof value === 'object' && !Array.isArray(value));

/** Owns ordered note writes and failed tab drafts independently of panel lifetime. */
export class NoteAutosaveCoordinator {
  private states = new Map<string, RecordState>();
  private historyRunning = false;
  private historyTail: Promise<unknown> = Promise.resolve();
  private accountListeners = new Set<() => void>();

  private state(owner: string): RecordState {
    const existing = this.states.get(owner);
    if (existing) return existing;
    let changes: Patch = {}, before: Patch = {};
    let titleDraft: NoteTitleDraft | null = null;
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(STORAGE_PREFIX + owner) || 'null');
      if (saved && isPatch(saved.changes) && isPatch(saved.before)) {
        changes = saved.changes;
        before = saved.before;
        if (typeof saved.titleDraft?.value === 'string' && typeof saved.titleDraft?.previous === 'string') titleDraft = saved.titleDraft;
      }
    } catch { /* Storage may be disabled or contain an incomplete prior draft. */ }
    const state: RecordState = {
      snapshot: { changes, before, titleDraft, status: Object.keys(changes).length ? 'failed' : 'idle', revision: 0 },
      pending: null, running: false, completion: null, listeners: new Set(),
    };
    this.states.set(owner, state);
    return state;
  }

  snapshot = (owner: string): NoteDraftSnapshot => this.state(owner).snapshot;

  subscribe(owner: string, listener: () => void): () => void {
    const state = this.state(owner);
    state.listeners.add(listener);
    return () => {
      state.listeners.delete(listener);
      if (!state.listeners.size && !state.running && state.snapshot.status === 'idle' && !state.snapshot.titleDraft) this.states.delete(owner);
    };
  }

  private publish(owner: string, state: RecordState, snapshot: NoteDraftSnapshot): void {
    state.snapshot = snapshot;
    try {
      if (snapshot.status === 'idle' && !snapshot.titleDraft) window.sessionStorage.removeItem(STORAGE_PREFIX + owner);
      else window.sessionStorage.setItem(STORAGE_PREFIX + owner, JSON.stringify({ changes: snapshot.changes, before: snapshot.before, titleDraft: snapshot.titleDraft }));
    } catch { /* In-memory recovery still works when tab storage is unavailable. */ }
    state.listeners.forEach(listener => listener());
    this.accountListeners.forEach(listener => listener());
  }

  stageTitle(owner: string, titleDraft: NoteTitleDraft | null): void {
    const state = this.state(owner);
    if (JSON.stringify(state.snapshot.titleDraft) === JSON.stringify(titleDraft)) return;
    this.publish(owner, state, { ...state.snapshot, titleDraft });
  }

  subscribeAccount = (listener: () => void): (() => void) => {
    this.accountListeners.add(listener);
    return () => { this.accountListeners.delete(listener); };
  };

  accountStatus(prefix: string): NoteSaveStatus {
    // Include interrupted tab drafts before their note panel has been reopened.
    try {
      for (let index = 0; index < window.sessionStorage.length; index++) {
        const key = window.sessionStorage.key(index);
        if (key?.startsWith(STORAGE_PREFIX + prefix)) this.state(key.slice(STORAGE_PREFIX.length));
      }
    } catch { /* In-memory status remains available with restricted storage. */ }
    let status: NoteSaveStatus = 'idle';
    for (const [owner, state] of this.states) {
      if (!owner.startsWith(prefix)) continue;
      if (state.snapshot.status === 'failed') return 'failed';
      if (state.snapshot.status === 'saving') status = 'saving';
      else if (state.snapshot.titleDraft && status === 'idle') status = 'pending';
    }
    return status;
  }

  enqueue(owner: string, changes: Patch, previous: Patch, write: NoteWriter): void {
    const state = this.state(owner);
    const before = { ...state.snapshot.before } as Record<string, unknown>;
    const previousValues = previous as Record<string, unknown>;
    for (const key of Object.keys(changes)) {
      if (!Object.prototype.hasOwnProperty.call(before, key)) before[key] = previousValues[key] ?? null;
    }
    const draft = structuredClone({ ...state.snapshot.changes, ...changes });
    const revision = state.snapshot.revision + 1;
    state.pending = { changes: draft, revision, write };
    this.publish(owner, state, { changes: draft, before: before as Patch, revision, status: 'saving', titleDraft: Object.prototype.hasOwnProperty.call(changes, 'title') ? null : state.snapshot.titleDraft });
    this.startDrain(owner, state);
  }

  retry(owner: string, write: NoteWriter): void {
    const state = this.state(owner);
    if (state.snapshot.status !== 'failed') return;
    this.enqueue(owner, state.snapshot.changes, state.snapshot.before, write);
  }

  undo(owner: string, write: NoteWriter): void {
    const state = this.state(owner);
    if (!Object.keys(state.snapshot.before).length) return;
    this.enqueue(owner, state.snapshot.before, state.snapshot.before, write);
  }

  private startDrain(owner: string, state: RecordState): void {
    if (!state.running && !this.historyRunning) state.completion = this.drain(owner, state);
  }

  /** Select history only after note saves finish; newer edits wait until replay completes. */
  withHistory<T>(action: () => Promise<T>): Promise<T> {
    const result = this.historyTail.then(async () => {
      while (Array.from(this.states.values()).some(state => state.running)) {
        await Promise.all(Array.from(this.states.values()).map(state => state.completion));
      }
      this.historyRunning = true;
      try { return await action(); }
      finally {
        this.historyRunning = false;
        for (const [owner, state] of this.states) if (state.pending) this.startDrain(owner, state);
      }
    });
    this.historyTail = result.catch(() => undefined);
    return result;
  }

  private async drain(owner: string, state: RecordState): Promise<void> {
    if (state.running) return;
    state.running = true;
    try {
      while (state.pending) {
        const request = state.pending;
        state.pending = null;
        try {
          await request.write(request.changes);
          if (request.revision === state.snapshot.revision) {
            this.publish(owner, state, { changes: {}, before: {}, revision: request.revision, status: 'idle', titleDraft: state.snapshot.titleDraft });
          }
        } catch {
          if (!state.pending) this.publish(owner, state, { ...state.snapshot, status: 'failed' });
        }
      }
    } finally {
      state.running = false;
      if (!state.listeners.size && state.snapshot.status === 'idle' && !state.snapshot.titleDraft) this.states.delete(owner);
    }
  }
}

export const noteAutosaveCoordinator = new NoteAutosaveCoordinator();
